import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PlayerList = ({ confirmedPlayers, pendingPlayers, declinedPlayers, maxPlayers, isAdmin }) => {
  const [activeSection, setActiveSection] = useState('confirmed');

  const PlayerCard = ({ player, type, showActions = false }) => {
    const getStatusIcon = () => {
      switch (type) {
        case 'confirmed':
          return { icon: 'CheckCircle', color: 'text-success' };
        case 'pending':
          return { icon: 'Clock', color: 'text-warning' };
        case 'declined':
          return { icon: 'XCircle', color: 'text-error' };
        default:
          return { icon: 'User', color: 'text-muted-foreground' };
      }
    };

    const status = getStatusIcon();

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '';
      return format(new Date(timestamp), "dd/MM 'às' HH:mm", { locale: ptBR });
    };

    return (
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50">
        <div className="flex items-center space-x-3">
          {/* Player Avatar */}
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
              {player?.avatar ? (
                <img
                  src={player?.avatar}
                  alt={player?.avatarAlt || `Avatar de ${player?.name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={18} className="text-muted-foreground" />
              )}
            </div>
            
            {/* Status Indicator */}
            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-background flex items-center justify-center border border-border`}>
              <Icon name={status?.icon} size={10} className={status?.color} />
            </div>
          </div>

          {/* Player Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium text-card-foreground truncate">
                {player?.name}
              </p>
              
              {/* Payment Status for Confirmed Players */}
              {type === 'confirmed' && (
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                  player?.isPaid 
                    ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                }`}>
                  {player?.isPaid ? 'Pago' : 'Pendente'}
                </span>
              )}
            </div>
            
            {/* Secondary Info */}
            <div className="flex items-center space-x-3 mt-1">
              {/* Reputation */}
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={12} className="text-warning" />
                <span className="text-xs text-muted-foreground">
                  {player?.reputation?.toFixed(1)}
                </span>
              </div>
              
              {/* Attendance History */}
              <span className="text-xs text-muted-foreground">
                {player?.attendanceHistory}% presença
              </span>
              
              {/* Position */}
              {player?.position && (
                <span className="text-xs text-muted-foreground">
                  {player?.position}
                </span>
              )}
            </div>
            
            {/* Timestamp */}
            <p className="text-xs text-muted-foreground mt-1">
              {type === 'confirmed' && formatTimestamp(player?.confirmedAt)}
              {type === 'pending' && `Convidado ${formatTimestamp(player?.invitedAt)}`}
              {type === 'declined' && `Recusou ${formatTimestamp(player?.declinedAt)}`}
            </p>
          </div>
        </div>
        {/* Actions */}
        {showActions && isAdmin && (
          <div className="flex items-center space-x-1 ml-2">
            {type === 'pending' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Check"
                  className="h-8 w-8 p-0 text-success hover:bg-success/10"
                  onClick={() => {
                    // Handle approve player
                    console.log('Approve player:', player?.id);
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  className="h-8 w-8 p-0 text-error hover:bg-error/10"
                  onClick={() => {
                    // Handle reject player
                    console.log('Reject player:', player?.id);
                  }}
                />
              </>
            )}
            
            {type === 'confirmed' && (
              <Button
                variant="ghost"
                size="sm"
                iconName="MoreVertical"
                className="h-8 w-8 p-0"
                onClick={() => {
                  // Handle more options
                  console.log('More options for player:', player?.id);
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const EmptyState = ({ type }) => {
    const getEmptyStateContent = () => {
      switch (type) {
        case 'confirmed':
          return {
            icon: 'Users',
            title: 'Nenhum jogador confirmado',
            description: 'Seja o primeiro a confirmar presença nesta pelada'
          };
        case 'pending':
          return {
            icon: 'Clock',
            title: 'Nenhum convite pendente',
            description: 'Todos os jogadores já responderam aos convites'
          };
        case 'declined':
          return {
            icon: 'UserMinus',
            title: 'Nenhuma recusa',
            description: 'Todos os jogadores convidados aceitaram ou estão pendentes'
          };
        default:
          return {
            icon: 'Users',
            title: 'Nenhum jogador',
            description: 'Lista vazia'
          };
      }
    };

    const content = getEmptyStateContent();

    return (
      <div className="text-center py-8">
        <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
          <Icon name={content?.icon} size={20} className="text-muted-foreground" />
        </div>
        <h4 className="font-medium text-card-foreground mb-1">
          {content?.title}
        </h4>
        <p className="text-sm text-muted-foreground">
          {content?.description}
        </p>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-card-foreground">
            Lista de Jogadores
          </h3>
          
          {/* Player Count Summary */}
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <span>{confirmedPlayers?.length || 0}/{maxPlayers}</span>
            <Icon name="Users" size={16} />
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-muted/50 rounded-full h-2 mb-4">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(((confirmedPlayers?.length || 0) / maxPlayers) * 100, 100)}%`
            }}
          />
        </div>

        {/* Section Tabs */}
        <div className="flex space-x-1 bg-muted/50 p-1 rounded-lg">
          <button
            onClick={() => setActiveSection('confirmed')}
            className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'confirmed' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="CheckCircle" size={14} className="text-success" />
            <span>Confirmados ({confirmedPlayers?.length || 0})</span>
          </button>
          
          <button
            onClick={() => setActiveSection('pending')}
            className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'pending' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Clock" size={14} className="text-warning" />
            <span>Pendentes ({pendingPlayers?.length || 0})</span>
          </button>
          
          <button
            onClick={() => setActiveSection('declined')}
            className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'declined' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="XCircle" size={14} className="text-error" />
            <span>Recusaram ({declinedPlayers?.length || 0})</span>
          </button>
        </div>
      </div>

      {/* Player Lists */}
      <div className="p-4">
        {/* Confirmed Players */}
        {activeSection === 'confirmed' && (
          <div className="space-y-3">
            {confirmedPlayers?.length > 0 ? (
              confirmedPlayers?.map((player) => (
                <PlayerCard
                  key={player?.id}
                  player={player}
                  type="confirmed"
                  showActions={isAdmin}
                />
              ))
            ) : (
              <EmptyState type="confirmed" />
            )}
          </div>
        )}

        {/* Pending Players */}
        {activeSection === 'pending' && (
          <div className="space-y-3">
            {pendingPlayers?.length > 0 ? (
              pendingPlayers?.map((player) => (
                <PlayerCard
                  key={player?.id}
                  player={player}
                  type="pending"
                  showActions={isAdmin}
                />
              ))
            ) : (
              <EmptyState type="pending" />
            )}
          </div>
        )}

        {/* Declined Players */}
        {activeSection === 'declined' && (
          <div className="space-y-3">
            {declinedPlayers?.length > 0 ? (
              declinedPlayers?.map((player) => (
                <div key={player?.id}>
                  <PlayerCard
                    player={player}
                    type="declined"
                    showActions={isAdmin}
                  />
                  {/* Decline Reason */}
                  {player?.declineReason && (
                    <div className="ml-13 mt-2 text-xs text-muted-foreground italic">
                      Motivo: {player?.declineReason}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <EmptyState type="declined" />
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {isAdmin && (
        <div className="p-4 border-t border-border">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              size="sm"
              iconName="UserPlus"
              iconPosition="left"
              className="flex-1"
              onClick={() => {
                // Handle invite players
                console.log('Invite players');
              }}
            >
              Convidar Jogadores
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Settings"
              iconPosition="left"
              className="flex-1"
              onClick={() => {
                // Handle manage players
                console.log('Manage players');
              }}
            >
              Gerenciar Lista
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerList;